/**
 * useSpeechRecognition — thin hook over the browser's built-in
 * `window.SpeechRecognition` / `webkitSpeechRecognition` API.
 *
 * Mirrors the design of useCoachVoice (zero plugins, graceful
 * degradation): exposes `supported`, `listening`, `transcript`, and
 * imperative `start` / `stop` / `reset`. Consumers wire a microphone
 * button to start/stop and read transcripts via an onResult callback
 * (delivered both on interim and final results so the chat input can
 * fill in live while the user speaks).
 *
 * Notes:
 *  - Chrome/Edge ship the prefixed `webkitSpeechRecognition`. Firefox
 *    has no implementation at time of writing — we report
 *    `supported: false` and the UI hides the mic button.
 *  - The API requires user gesture + HTTPS (preview URL is HTTPS, so
 *    permission flow is the standard Chrome mic prompt).
 *  - We use continuous + interimResults so longer reflections don't
 *    truncate after a pause.
 *
 * Pause handling — the part that matters for hands-free UX
 * ────────────────────────────────────────────────────────
 * The browser finalises a result as soon as you take a natural
 * breath. If consumers auto-send on `isFinal`, even ordinary
 * commas get treated as "I'm done talking". To fix that:
 *   1. We accumulate ALL finalised segments across the session so
 *      a mid-sentence pause never loses the words you already
 *      spoke. The previous implementation iterated from
 *      `e.resultIndex` only, which silently dropped earlier
 *      finals — pauses appeared to "reset" the transcript.
 *   2. We expose a `silenceMs` option (default **2.5s**) — only
 *      after the recogniser stays quiet for that long do we emit
 *      `{ silenceEnd: true }` so consumers know it's safe to
 *      auto-submit. Every new interim/final result resets the
 *      timer, so steady talking — even with comma-length pauses —
 *      stays in a single utterance.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

function getRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function useSpeechRecognition({
  lang = 'en-US',
  silenceMs = 2500,
} = {}) {
  const Ctor = getRecognitionCtor();
  const supported = !!Ctor;

  const recRef = useRef(null);
  const onResultRef = useRef(null);
  // Accumulated finalised text across the whole listening session.
  // The browser's `e.resultIndex` only points at the latest changed
  // chunk; without this buffer, earlier finals get silently dropped
  // every time the user pauses long enough for a finalisation.
  const finalsRef = useRef('');
  // Silence-detection debounce. Reset on every result; fires the
  // consumer callback with `{ silenceEnd: true }` once the user has
  // been quiet for `silenceMs` so hands-free mode can auto-submit
  // a complete utterance rather than a single comma-bounded phrase.
  const silenceTimerRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);

  const clearSilence = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  // Lazy-init the recognition instance so we don't request mic
  // permission until the user actually clicks the button.
  const ensureInstance = useCallback(() => {
    if (!supported) return null;
    if (recRef.current) return recRef.current;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      // Walk every result that finalised since the previous
      // ``onresult`` event and append it to the persistent buffer.
      // We never overwrite — only append — so a pause inside a long
      // sentence cannot erase the words that came before it.
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) finalsRef.current += (r[0]?.transcript || '');
      }
      // Interim chunk = the latest unfinalised utterance (rebuilt
      // every event by the engine, so it's safe to drop the prior
      // interim when a new one arrives).
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (!r.isFinal) interim += (r[0]?.transcript || '');
      }
      const combined = (finalsRef.current + interim).replace(/\s+/g, ' ').trim();
      setTranscript(combined);
      // Live tick — `silenceEnd: false` so consumers update the
      // input box live but DON'T auto-submit yet.
      onResultRef.current?.(combined, { silenceEnd: false });

      // (Re)arm the silence detector. Auto-submit only fires after
      // the user has stayed quiet long enough that we're confident
      // they've finished their thought.
      clearSilence();
      if (combined.length > 0) {
        silenceTimerRef.current = setTimeout(() => {
          onResultRef.current?.(combined, { silenceEnd: true });
        }, silenceMs);
      }
    };
    rec.onerror = (e) => {
      setError(e?.error || 'unknown');
      setListening(false);
      clearSilence();
    };
    rec.onend = () => {
      setListening(false);
      clearSilence();
    };
    recRef.current = rec;
    return rec;
  }, [Ctor, lang, supported, silenceMs, clearSilence]);

  const start = useCallback((onResult) => {
    const rec = ensureInstance();
    if (!rec) return;
    onResultRef.current = onResult || null;
    setError(null);
    setTranscript('');
    finalsRef.current = '';
    clearSilence();
    try {
      rec.start();
      setListening(true);
    } catch (e) {
      // Most common case: start() called while already listening —
      // safe to ignore and treat as a no-op.
      setError(e?.message || 'start_failed');
    }
  }, [ensureInstance, clearSilence]);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* idempotent */ }
    setListening(false);
    clearSilence();
  }, [clearSilence]);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
    finalsRef.current = '';
    clearSilence();
  }, [clearSilence]);

  // Cleanup on unmount — stop any active session so the mic indicator
  // disappears immediately when the user navigates away.
  useEffect(() => () => {
    const rec = recRef.current;
    if (rec) {
      try { rec.abort(); } catch { /* noop */ }
    }
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
  }, []);

  return { supported, listening, transcript, error, start, stop, reset };
}
